import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState } from 'react';
import { LayoutDashboard, Gem, Users, Bell } from 'lucide-react';
import { listStoneRequirements, listStoneQuotes } from '@/utils/api';

const LAST_SEEN_KEY = (userId) => `stones_notifications_last_seen_${userId || 'anon'}`;

// Persistent sub-nav for the Stones module.
// - Admin sees: Dashboard, Requirements, Notifications, Suppliers
// - Suppliers see: Requirements, Notifications
export default function StoneTabs({ active }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [unread, setUnread] = useState(0);

  // Lightweight unread count for the badge. Pulls reqs + quotes once per mount
  // and again every 60s so the badge stays current without a full notifications
  // backend. The notifications page itself does the heavier feed build.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const lastSeen = parseInt(localStorage.getItem(LAST_SEEN_KEY(user.id)) || '0', 10);
    const tick = async () => {
      try {
        const [r, q] = await Promise.all([listStoneRequirements(), listStoneQuotes()]);
        if (cancelled) return;
        const reqs = r.data.data || [];
        const quotes = q.data.data || [];
        let count = 0;
        const isSupplier = user.role === 'stone_supplier';
        if (isAdmin) {
          for (const qq of quotes) {
            const ts = new Date(qq.updated_at || qq.created_at).getTime();
            if (qq.status === 'submitted' && ts > lastSeen) count++;
          }
        }
        if (isSupplier) {
          for (const rr of reqs) {
            const ts = new Date(rr.created_at).getTime();
            if (rr.status === 'open' && ts > lastSeen && !quotes.find(qq => qq.requirement_id === rr.id)) count++;
          }
          for (const qq of quotes) {
            if (qq.status === 'revise_requested' && qq.revise_request?.requested_at) {
              const ts = new Date(qq.revise_request.requested_at).getTime();
              if (ts > lastSeen) count++;
            }
          }
        }
        setUnread(count);
      } catch {}
    };
    tick();
    const intervalId = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [user, isAdmin]);

  // Tabs visible to current role
  const tabs = [
    isAdmin && { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/stones/dashboard' },
    { key: 'requirements', label: 'Requirements', icon: Gem, path: '/stones' },
    { key: 'notifications', label: 'Notifications', icon: Bell, path: '/stones/notifications', badge: unread },
    isAdmin && { key: 'suppliers', label: 'Suppliers', icon: Users, path: '/stones/suppliers' },
  ].filter(Boolean);

  // Auto-detect active tab from URL if not explicitly given.
  const path = location.pathname;
  const detected = active || (
    path.startsWith('/stones/dashboard') ? 'dashboard' :
    path.startsWith('/stones/suppliers') ? 'suppliers' :
    path.startsWith('/stones/notifications') ? 'notifications' :
    'requirements'
  );

  return (
    <div className="bg-white border-b border-slate-200 sticky top-12 z-30">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="flex gap-1 overflow-x-auto -mx-1 px-1">
          {tabs.map(t => {
            const Icon = t.icon;
            const isActive = detected === t.key;
            return (
              <button
                key={t.key}
                onClick={() => navigate(t.path)}
                className={`relative flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'text-teal-700 border-teal-600'
                    : 'text-slate-500 border-transparent hover:text-slate-800'
                }`}
                data-testid={`stones-tab-${t.key}`}
              >
                <Icon size={14} />
                {t.label}
                {t.badge > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-teal-600 text-white text-[10px] font-semibold">
                    {t.badge > 99 ? '99+' : t.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
